/**
 * South African calendar dates.
 *
 * `new Date().toISOString().slice(0, 10)` is the UTC date, not the local
 * one. South Africa is UTC+2 year-round, so between midnight and 02:00
 * SAST that expression returns YESTERDAY. Every deadline comparison,
 * every "is this overdue", every default date on a form and every
 * export filename built that way is wrong for the first two hours of
 * each day — quietly, and only for the people working late or early.
 *
 * SAST has no daylight saving (fixed UTC+2), so a constant offset is
 * exact rather than an approximation.
 *
 * Use these helpers for anything a South African user will read as a
 * date. Do NOT use them to build a Date for arithmetic — compose the
 * arithmetic in milliseconds and format the result at the end.
 */
/** SAST is UTC+2 all year. No daylight saving to account for. */
export const SAST_OFFSET_MS = 2 * 60 * 60 * 1000;
export const SA_TIME_ZONE = 'Africa/Johannesburg';
function shiftToSast(instant) {
    return new Date(instant.getTime() + SAST_OFFSET_MS);
}
function pad(n) {
    return String(n).padStart(2, '0');
}
/** The SA calendar date of an instant, as yyyy-mm-dd. */
export function saDateOf(instant) {
    const d = shiftToSast(instant);
    return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}
/** Today in South Africa, as yyyy-mm-dd. */
export function saToday(now = new Date()) {
    return saDateOf(now);
}
/**
 * A SA calendar date `days` from now, as yyyy-mm-dd. Negative values
 * look backwards. Safe across month and year boundaries.
 */
export function saDatePlusDays(days, now = new Date()) {
    return saDateOf(new Date(now.getTime() + days * 86_400_000));
}
/** The current SA month, as yyyy-mm — the period key used by M&E entries. */
export function saMonth(now = new Date()) {
    return saToday(now).slice(0, 7);
}
/** The SA month of an instant, as yyyy-mm. */
export function saMonthOf(instant) {
    return saDateOf(instant).slice(0, 7);
}
/**
 * A SA wall-clock stamp, as `yyyy-mm-dd hh:mm`. For activity notes and
 * audit trails, where a UTC time reads as an hour that never happened.
 */
export function saDateTime(now = new Date()) {
    const d = shiftToSast(now);
    return `${saDateOf(now)} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
}
/**
 * True when `iso` is yyyy-mm-dd and names a day that exists. The shape
 * alone lets through 2027-02-31 and 2027-31-12, and Date.UTC quietly
 * moves the first into March, so the parts are checked to come back
 * unchanged.
 */
export function isCalendarDate(iso) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
    if (!m)
        return false;
    const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
    const back = new Date(Date.UTC(y, mo - 1, d));
    // Date.UTC maps years 0 to 99 onto 1900 to 1999, which also fails here.
    return back.getUTCFullYear() === y && back.getUTCMonth() === mo - 1 && back.getUTCDate() === d;
}
/**
 * Whole days from today (SA) until an ISO date. Negative means overdue.
 * Both sides are calendar dates, so this never drifts by an hour.
 */
export function saDaysUntil(iso, now = new Date()) {
    const target = utcMidnightOf(iso);
    const today = utcMidnightOf(saToday(now));
    if (target === null || today === null)
        return Number.NaN;
    return Math.round((target - today) / 86_400_000);
}
/** yyyy-mm-dd to a UTC-midnight timestamp, or null if unparseable. */
function utcMidnightOf(iso) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
    if (!m)
        return null;
    return Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}
